<template>
  <div class="businessunitPanel bp">
    <!-- ── Hero ── -->
    <div class="bp-hero">
      <div class="bp-hero__avatar">
        <img v-if="businessunit.logoLink" :src="businessunit.logoLink" alt=""/>
        <span v-else>{{ (businessunit.name || '?').charAt(0).toUpperCase() }}</span>
      </div>
      <div class="bp-hero__meta">
        <div class="bp-hero__name">{{ businessunit.name || msg('New Branch') }}</div>
        <div class="bp-hero__sub">
          <span v-if="businessunit.id" class="bp-hero__id">#{{ businessunit.id }}</span>
          <span v-if="businessunit.cur" class="bp-hero__chip">{{ businessunit.cur }}</span>
          <span v-if="businessunit.externalApiType" class="bp-hero__chip">{{ businessunit.externalApiType }}</span>
        </div>
      </div>
    </div>

    <b-tabs class="tabs-title bp-tabs">

      <!-- ── Main settings tab ── -->
      <b-tab :title="branchAlias" active>
        <div class="mt-2" v-if="loaded">
          <validation-observer ref="refFormObserver">

            <!-- ═══════════════════════════════════════════════════
                 SECTION 1 — Contact Information
            ═══════════════════════════════════════════════════ -->
            <b-card no-body class="bp-section bp-section--primary mb-2">
              <div class="bp-section__head">
                <div class="bp-section__icon bp-section__icon--primary">
                  <feather-icon icon="UserIcon" size="17"/>
                </div>
                <div>
                  <div class="bp-section__title">{{ msg('Contact Information') }}</div>
                  <div class="bp-section__sub">{{ msg('Name, contact person and how to reach this branch') }}</div>
                </div>
              </div>
              <b-card-body class="bp-section__body">
                <b-row>
                  <b-col cols="12" md="6" lg="4">
                    <b-form-group :label="msg('Name')" label-for="bu-name">
                      <validation-provider #default="{ errors }" :name="msg('Name')" rules="required">
                        <b-form-input id="bu-name" v-model="businessunit.name"
                                      :state="errors.length > 0 ? false : null"/>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="4">
                    <b-form-group :label="msg('Contact fullname')" label-for="bu-contact">
                      <validation-provider #default="{ errors }" :name="msg('Contact fullname')" rules="required">
                        <b-form-input id="bu-contact" v-model="businessunit.contact"
                                      :state="errors.length > 0 ? false : null"/>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="4">
                    <b-form-group :label="msg('Contact address')" label-for="bu-address">
                      <validation-provider #default="{ errors }" :name="msg('Contact address')" rules="required">
                        <b-form-input id="bu-address" v-model="businessunit.address"
                                      :state="errors.length > 0 ? false : null"/>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="4">
                    <b-form-group :label="msg('Contact phone')" label-for="bu-phone">
                      <validation-provider #default="{ errors }" :name="msg('Contact phone')" rules="required">
                        <b-form-input id="bu-phone" v-model="businessunit.phone"
                                      :state="errors.length > 0 ? false : null"/>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="4">
                    <b-form-group :label="msg('Contact email')" label-for="bu-email">
                      <validation-provider #default="{ errors }" :name="msg('Contact email')" rules="required">
                        <b-form-input id="bu-email" v-model="businessunit.email"
                                      :state="errors.length > 0 ? false : null"/>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="4">
                    <b-form-group :label="msg('Website')" label-for="bu-website">
                      <b-form-input id="bu-website" v-model="businessunit.website"/>
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-card-body>
            </b-card>

            <!-- ═══════════════════════════════════════════════════
                 SECTION 2 — Financial Settings
            ═══════════════════════════════════════════════════ -->
            <b-card no-body class="bp-section bp-section--success mb-2">
              <div class="bp-section__head">
                <div class="bp-section__icon bp-section__icon--success">
                  <feather-icon icon="DollarSignIcon" size="17"/>
                </div>
                <div>
                  <div class="bp-section__title">{{ msg('Financial Settings') }}</div>
                  <div class="bp-section__sub">{{ msg('Currency, group, fees and branding') }}</div>
                </div>
              </div>
              <b-card-body class="bp-section__body">
                <b-row>
                  <b-col cols="12" md="6" lg="4">
                    <b-form-group :label="msg('Currency')" label-for="bu-currency">
                      <v-select
                          id="bu-currency"
                          :dir="isRTL ? 'rtl' : 'ltr'"
                          v-model="businessunit.cur"
                          :options="[
                            { value: 'USD', label: msg('DOLLAR') },
                            { value: 'EUR', label: msg('EURO') },
                            { value: 'ILS', label: msg('SHEKEL') },
                            { value: 'GBP', label: msg('POUND') },
                          ]"
                          class="w-100"
                          :reduce="t => t.value"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="4">
                    <b-form-group :label="groupAlias" label-for="bu-group">
                      <v-select
                          id="bu-group"
                          :dir="isRTL ? 'rtl' : 'ltr'"
                          v-model="businessunit.group"
                          :options="allGroups"
                          class="w-100"
                          label="name"
                          :reduce="g => g.id"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="4">
                    <b-form-group :label="msg('External Payment Link')" label-for="bu-extlink">
                      <b-form-input id="bu-extlink" v-model="businessunit.externalLink"/>
                    </b-form-group>
                  </b-col>
                </b-row>

                <!-- Logo -->
                <b-row>
                  <b-col cols="12" md="8" lg="6">
                    <b-form-group label="Hotel Logo URL" label-for="bu-logo">
                      <b-form-input id="bu-logo" v-model="businessunit.logoLink"
                                    placeholder="https://example.com/logo.png"/>
                    </b-form-group>
                    <div v-if="businessunit.logoLink" class="mt-n50 mb-2 bp-logo-preview">
                      <img :src="businessunit.logoLink" alt="Logo preview"/>
                    </div>
                  </b-col>
                </b-row>

                <!-- ── Management Fee (mutual exclusive) ── -->
                <hr class="my-1"/>
                <div class="fee-section">
                  <div class="d-flex align-items-center mb-1">
                    <feather-icon icon="PercentIcon" size="15" class="mr-50 text-primary"/>
                    <span class="font-weight-bold mr-1">{{ msg('Management Fee') }}</span>
                    <b-badge variant="light-info" class="small">{{ msg('Select one method') }}</b-badge>
                  </div>

                  <b-form-group class="mb-1">
                    <b-form-radio-group
                        v-model="feeMode"
                        :options="feeModeOptions"
                        buttons
                        button-variant="outline-primary"
                        size="sm"
                        @change="onFeeModeChange"
                    />
                  </b-form-group>

                  <b-row>
                    <b-col v-if="feeMode === 'percentage'" cols="12" md="6" lg="4">
                      <b-form-group
                          :label="msg('Management fee (%)')"
                          :description="msg('Percentage of monthly net revenue')"
                      >
                        <b-input-group append="%">
                          <b-form-input
                              type="number"
                              step="0.5"
                              min="0"
                              max="100"
                              v-model="businessunit.managementFee"
                              placeholder="e.g. 15"
                          />
                        </b-input-group>
                      </b-form-group>
                    </b-col>

                    <b-col v-if="feeMode === 'perUnit'" cols="12" md="6" lg="4">
                      <b-form-group
                          :label="msg('Management fee per unit (€)')"
                          :description="msg('Fixed amount per active property per month')"
                      >
                        <b-input-group append="€">
                          <b-form-input
                              type="number"
                              step="0.5"
                              min="0"
                              v-model="businessunit.unitPrice"
                              placeholder="e.g. 50"
                          />
                        </b-input-group>
                      </b-form-group>
                    </b-col>

                    <b-col cols="12" md="6" lg="4">
                      <b-form-group
                          :label="msg('Setup fee (one-time) (€)')"
                          :description="msg('One-time onboarding fee charged to the client')"
                      >
                        <b-input-group append="€">
                          <b-form-input
                              type="number"
                              step="0.5"
                              min="0"
                              v-model="businessunit.branchExt.setupFee"
                              placeholder="e.g. 500"
                          />
                        </b-input-group>
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <div class="fee-hint d-flex align-items-center">
                    <feather-icon icon="InfoIcon" size="14" class="mr-50 fee-hint__icon"/>
                    <small v-if="feeMode === 'percentage'">
                      {{ msg('The percentage fee is calculated on the monthly net revenue of all contracts.') }}
                    </small>
                    <small v-else>
                      {{ msg('The per-unit fee is a fixed amount multiplied by the number of active properties in this branch.') }}
                    </small>
                  </div>
                </div>
              </b-card-body>
            </b-card>

            <!-- ═══════════════════════════════════════════════════
                 SECTION 3 — API & Integrations
            ═══════════════════════════════════════════════════ -->
            <b-card no-body class="bp-section bp-section--warning mb-2">
              <div class="bp-section__head">
                <div class="bp-section__icon bp-section__icon--warning">
                  <feather-icon icon="LinkIcon" size="17"/>
                </div>
                <div>
                  <div class="bp-section__title">{{ msg('API & Integrations') }}</div>
                  <div class="bp-section__sub">{{ msg('Channel manager, keybox provider and credentials') }}</div>
                </div>
              </div>
              <b-card-body class="bp-section__body">
                <b-row>
                  <b-col cols="12" md="6" lg="3">
                    <b-form-group label="External API" label-for="bu-extapi">
                      <v-select
                          id="bu-extapi"
                          v-model="businessunit.externalApiType"
                          :options="['HOSTAWAY', 'RENTAL', 'APALEO', 'CLOUDBEDS', 'SITEMINDER']"
                          :clearable="false"
                          placeholder="Select an option"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="3">
                    <b-form-group label="KeyBox Provider" label-for="bu-kbprovider">
                      <v-select
                          id="bu-kbprovider"
                          v-model="businessunit.branchExt.keyBoxProvider"
                          :options="['Quipeng', 'Keynest']"
                          :clearable="false"
                          placeholder="Select an option"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="3">
                    <b-form-group :label="msg('Price per Keybox (€)')" label-for="bu-kbprice">
                      <b-input-group append="€">
                        <b-form-input
                            id="bu-kbprice"
                            type="number"
                            step="0.01"
                            min="0"
                            v-model="businessunit.branchExt.keyboxPrice"
                            placeholder="e.g. 5.00"
                        />
                      </b-input-group>
                      <small class="text-muted">{{ msg('Monthly keybox cost per building') }}</small>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="3"/>
                </b-row>

                <b-row>
                  <b-col cols="12" md="6" lg="6">
                    <b-form-group :label="msg('Channel ID')" label-for="bu-channelid">
                      <b-form-input id="bu-channelid" v-model="businessunit.channelId"/>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="6">
                    <b-form-group :label="msg('Channel SECRET')" label-for="bu-channelsecret">
                      <b-form-input id="bu-channelsecret" v-model="businessunit.channelSecret"/>
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-card-body>
            </b-card>

            <!-- ═══════════════════════════════════════════════════
                 SECTION 4 — Messaging (Twilio)
            ═══════════════════════════════════════════════════ -->
            <b-card no-body class="bp-section bp-section--info mb-2">
              <div class="bp-section__head">
                <div class="bp-section__icon bp-section__icon--info">
                  <feather-icon icon="MessageSquareIcon" size="17"/>
                </div>
                <div>
                  <div class="bp-section__title">{{ msg('Messaging (Twilio)') }}</div>
                  <div class="bp-section__sub">{{ msg('SMS and WhatsApp sender credentials') }}</div>
                </div>
              </div>
              <b-card-body class="bp-section__body">
                <b-row>
                  <b-col cols="12" md="6" lg="3">
                    <b-form-group :label="msg('Twillio ID')" label-for="bu-twillioid">
                      <b-form-input id="bu-twillioid" v-model="businessunit.twillioId"/>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="3">
                    <b-form-group :label="msg('Twillio SECRET')" label-for="bu-twilliosecret">
                      <b-form-input id="bu-twilliosecret" v-model="businessunit.twillioSecret"/>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="3">
                    <b-form-group :label="msg('Twillio Phone sender')" label-for="bu-twillioPhone">
                      <b-form-input id="bu-twillioPhone" v-model="businessunit.twillioPhoneSender"/>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6" lg="3">
                    <b-form-group :label="msg('Twillio Whatsapp sender')" label-for="bu-twillioWa">
                      <b-form-input id="bu-twillioWa" v-model="businessunit.twillioWhatsappSender"/>
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-card-body>
            </b-card>

          </validation-observer>
        </div>

        <!-- Error state -->
        <div v-else-if="error != null">
          <b-alert show variant="danger">
            <feather-icon icon="AlertTriangleIcon" size="16" class="mr-75"/>
            <strong>{{ error }}</strong>
          </b-alert>
        </div>

        <!-- Loading state -->
        <div v-else>
          <div style="min-height: 300px; text-align: center">
            <div style="margin-top: 20%">
              <b-spinner label="Spinning"/>
              <p class="mt-1 text-muted">{{ msg('loading') }}</p>
            </div>
          </div>
        </div>
      </b-tab>

      <!-- ── Opening times tab ── -->
      <b-tab title="Opening times">
        <perfect-scrollbar style="max-height: 550px">
          <branch-time v-model="businessunit.openingTime"/>
        </perfect-scrollbar>
      </b-tab>

    </b-tabs>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import BranchTime from '@/components/panels/branches/BranchTime'

export default {
  name: 'branchPanel',
  components: { BranchTime },
  data() {
    return {
      openingTime: {},
      loaded: false,
      error: null,
      feeMode: 'percentage',
      businessunit: {
        cur: 'EUR',
        id: 0,
        group: null,
        kycid: 0,
        mailid: 0,
        name: '',
        orgid: 1,
        companyClients: 0,
        externalLink: '',
        website: '',
        openingTime: {},
        branchExt: { keyBoxProvider: null, keyboxPrice: null },
      },
      listeners: {
        submit: () => {},
      },
    }
  },
  props: {
    id: Number,
  },
  watch: {
    id() {
      this.onLoad()
      this.error = null
    },
  },
  computed: {
    ...mapGetters('translator', ['msg']),
    ...mapGetters('branches', ['getGroups']),
    ...mapGetters('clients', ['groupAlias', 'branchAlias']),

    feeModeOptions() {
      return [
        { text: `${this.msg('Percentage')} (%)`, value: 'percentage' },
        { text: `${this.msg('Per Unit')} (€)`, value: 'perUnit' },
      ]
    },

    allGroups() {
      return Object.keys(this.getGroups).map(id => ({
        id: parseInt(id),
        name: this.getGroups[id],
      }))
    },
  },
  created() {
    this.onLoad()
    this.$root.$on('crm::submitBusinessUnit', (this.listeners.submit = () => {
      this.submit()
    }))
  },
  beforeDestroy() {
    this.$root.$off('crm::submitBusinessUnit', this.listeners.submit)
  },
  methods: {
    ...mapActions('branches', { _getBusinessUnit: 'get', _updateBusinessUnit: 'update', _register: 'register' }),
    ...mapActions('branches', { _createRentalAccount: 'createRentalAccount' }),

    onFeeModeChange(newMode) {
      if (newMode === 'percentage') {
        this.businessunit.unitPrice = null
      } else {
        this.businessunit.managementFee = null
      }
    },

    detectFeeMode() {
      if (this.businessunit.unitPrice != null && this.businessunit.unitPrice > 0) {
        this.feeMode = 'perUnit'
      } else {
        this.feeMode = 'percentage'
      }
    },

    onLoad() {
      this.loaded = false
      if (this.id === 0 || typeof this.id !== 'number') {
        this.businessunit = {
          id: 0,
          group: null,
          cur: 'EUR',
          externalLink: '',
          website: '',
          branchExt: { keyBoxProvider: null, keyboxPrice: null },
        }
        this.feeMode = 'percentage'
        this.loaded = true
      } else {
        this.loadBusinessUnit()
      }
    },

    loadBusinessUnit() {
      this.loaded = false
      this._getBusinessUnit({ id: this.id })
          .then(businessunit => {
            if (!businessunit.branchExt) {
              businessunit.branchExt = { keyBoxProvider: null, keyboxPrice: null }
            }
            this.businessunit = businessunit
            this.detectFeeMode()
            this.loaded = true
          }, error => {
            this.error = error
          })
    },

    createBusinessUnit() {
      this._register({ branch: this.businessunit })
          .then(
              id => this.$emit('submitted', { status: true, server: id, id }),
              a => this.$emit('submitted', { status: false, server: a }),
          )
    },

    updateBusinessUnit() {
      this._updateBusinessUnit({ id: this.id, branch: this.businessunit })
          .then(
              a => this.$emit('submitted', { status: true, server: a }),
              a => this.$emit('submitted', { status: false, server: a }),
          )
    },

    submit() {
      if (this.id !== 0) return this.updateBusinessUnit()
      return this.createBusinessUnit()
    },
  },
}
</script>

<style scoped>
/* ── Hero ── */
.bp-hero {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  margin-bottom: 14px;
  border-radius: 14px;
  background: linear-gradient(135deg, #1A2332 0%, #243447 55%, #2D4156 100%);
  color: #fff;
}
.bp-hero__avatar {
  flex-shrink: 0;
  width: 52px; height: 52px;
  border-radius: 13px;
  display: flex; align-items: center; justify-content: center;
  font-size: 20px; font-weight: 800;
  color: #1A2332;
  background: linear-gradient(135deg, #EDCFA0, #D4A96A);
  overflow: hidden;
}
.bp-hero__avatar img { width: 100%; height: 100%; object-fit: cover; }
.bp-hero__name { font-size: 18px; font-weight: 800; letter-spacing: -0.3px; line-height: 1.2; }
.bp-hero__sub { display: flex; align-items: center; gap: 8px; margin-top: 4px; flex-wrap: wrap; }
.bp-hero__id { font-size: 12px; color: rgba(255,255,255,0.6); font-weight: 600; }
.bp-hero__chip {
  font-size: 10.5px; font-weight: 800; letter-spacing: 0.4px;
  padding: 2px 8px; border-radius: 7px;
  background: rgba(212, 169, 106, 0.22); color: #EDCFA0;
  text-transform: uppercase;
}

/* ── Section card ── */
.bp-section {
  border: 1px solid rgba(34, 41, 47, 0.08) !important;
  border-radius: 14px !important;
  box-shadow: 0 2px 10px rgba(34, 41, 47, 0.05) !important;
  overflow: hidden;
}
.bp-section__head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(34, 41, 47, 0.06);
}
.bp-section__icon {
  flex-shrink: 0;
  width: 40px; height: 40px;
  border-radius: 11px;
  display: flex; align-items: center; justify-content: center;
  color: #fff;                         /* white icon on a solid color = strong contrast */
}
.bp-section__icon--primary { background: #6f5ff0; box-shadow: 0 4px 10px rgba(115,103,240,0.40); }
.bp-section__icon--success { background: #1fc26b; box-shadow: 0 4px 10px rgba(40,199,111,0.40); }
.bp-section__icon--warning { background: #ff9226; box-shadow: 0 4px 10px rgba(255,159,67,0.40); }
.bp-section__icon--info    { background: #00bcd9; box-shadow: 0 4px 10px rgba(0,207,232,0.40); }
.bp-section__title { font-size: 14.5px; font-weight: 800; color: #2f2d3d; letter-spacing: -0.1px; }
.bp-section__sub { font-size: 11.5px; color: #7c7a89; font-weight: 500; margin-top: 1px; }
.bp-section__body { padding: 16px 18px; }

/* Per-section color accent (top stripe + tinted header band) for clear,
   high-contrast separation between categories. */
.bp-section--primary { border-top: 3px solid #6f5ff0 !important; }
.bp-section--success { border-top: 3px solid #1fc26b !important; }
.bp-section--warning { border-top: 3px solid #ff9226 !important; }
.bp-section--info    { border-top: 3px solid #00bcd9 !important; }
.bp-section--primary .bp-section__head { background: rgba(115,103,240,0.07); }
.bp-section--success .bp-section__head { background: rgba(40,199,111,0.07); }
.bp-section--warning .bp-section__head { background: rgba(255,159,67,0.08); }
.bp-section--info    .bp-section__head { background: rgba(0,207,232,0.07); }

/* Labels + inputs polish */
.bp ::v-deep .bp-section__body label {
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #6e6b7b;
  margin-bottom: 5px;
}
.bp ::v-deep .bp-section__body .form-control,
.bp ::v-deep .bp-section__body .vs__dropdown-toggle {
  border-radius: 9px;
}
.bp ::v-deep .bp-section__body .form-group { margin-bottom: 1rem; }

/* ── Fee panel ── */
.fee-section {
  background: rgba(115, 103, 240, 0.05);
  border: 1px solid rgba(115, 103, 240, 0.14);
  border-radius: 12px;
  padding: 16px;
}
.fee-hint {
  background: rgba(0, 149, 255, 0.06);
  border: 1px solid rgba(0, 149, 255, 0.16);
  border-radius: 9px;
  padding: 9px 12px;
  color: #5e5873;
}
.fee-hint__icon { color: #00a8ff; flex-shrink: 0; }

/* Logo preview */
.bp-logo-preview {
  display: inline-block;
  padding: 6px 10px;
  border-radius: 9px;
  background: #f8f9fa;
  border: 1px solid rgba(34,41,47,0.08);
}
.bp-logo-preview img { max-height: 46px; max-width: 180px; object-fit: contain; display: block; }

.customColorLabelWhatsapp {
  color: #25D366 !important;
  font-weight: bold;
}

/* Tabs */
.bp-tabs ::v-deep .nav-tabs { margin-bottom: 0; }

/* ════════════════════════════════════════════════════════════
   DARK LAYOUT — explicit styling for every surface in the modal
   ════════════════════════════════════════════════════════════ */
.dark-layout .bp-section {
  background: #283046 !important;
  border-color: rgba(255,255,255,0.07) !important;
  box-shadow: 0 2px 12px rgba(0,0,0,0.25) !important;
}
.dark-layout .bp-section ::v-deep .card-body { background: transparent; }
.dark-layout .bp-section__head { border-bottom-color: rgba(255,255,255,0.08); }
/* Slightly stronger header tints so the per-section color reads on dark */
.dark-layout .bp-section--primary .bp-section__head { background: rgba(115,103,240,0.16); }
.dark-layout .bp-section--success .bp-section__head { background: rgba(40,199,111,0.16); }
.dark-layout .bp-section--warning .bp-section__head { background: rgba(255,159,67,0.16); }
.dark-layout .bp-section--info    .bp-section__head { background: rgba(0,207,232,0.16); }
.dark-layout .bp-section__title { color: #d0d2d6; }
.dark-layout .bp-section__sub { color: #9b9aa8; }

/* Labels + inputs */
.dark-layout .bp ::v-deep .bp-section__body label { color: #b4b7bd; }
.dark-layout .bp ::v-deep .form-control {
  background: #1f2638;
  border-color: rgba(255,255,255,0.10);
  color: #d0d2d6;
}
.dark-layout .bp ::v-deep .form-control::placeholder { color: #6e7180; }
.dark-layout .bp ::v-deep .input-group-text {
  background: #161d29;
  border-color: rgba(255,255,255,0.10);
  color: #b4b7bd;
}
.dark-layout .bp ::v-deep .vs__dropdown-toggle {
  background: #1f2638;
  border-color: rgba(255,255,255,0.10);
}
.dark-layout .bp ::v-deep .vs__selected,
.dark-layout .bp ::v-deep .vs__search { color: #d0d2d6; }

/* Fee panel + hint */
.dark-layout .fee-section { background: rgba(115,103,240,0.12); border-color: rgba(115,103,240,0.26); }
.dark-layout .fee-hint {
  background: rgba(0,149,255,0.10);
  border-color: rgba(0,149,255,0.22);
  color: #b4b7bd;
}
.dark-layout .bp ::v-deep hr { border-top-color: rgba(255,255,255,0.08); }

/* Logo preview stays on a light chip so logos remain legible */
.dark-layout .bp-logo-preview { background: #eef0f4; border-color: rgba(255,255,255,0.10); }
</style>
